import { z } from 'zod';
import {
  LetterLayout,
  LetterLibraryType,
  LetterSignerRole,
  LetterTemplateFormat,
  LetterTemplateNote,
  LetterTemplateStatus,
  LetterTemplateView,
  LetterVariant,
} from './letters.js';

/**
 * A letter pack (APP-130 slice 2): one .zip holding `manifest.json` and the Word or PDF letter
 * templates it lists. Settings checks it first and writes nothing (D19), then imports each new file
 * as a draft through the same path as a single upload. A manifest that says `"approval":
 * "approved"` has its new letters approved and used once their virus check and sample are done
 * (APP-205, overruling D20 for such packs); one that says `draft`, or nothing, stays drafts. The
 * manifest says, per file, the same facts as the upload form (`UploadLetterTemplateFields`), and
 * names the signer the way the starting setup file names people (APP-118 D3). The starting setup
 * file names a pack by its SHA-256 (`letters.pack`), and its .zip travels beside the file.
 */
export const LETTER_PACK_FORMAT = 'myrewards.letter-pack';
export const LETTER_PACK_VERSION = 1;

/**
 * The pack's safe limits. A letter template is up to 10 MB (APP-035); a Word file is already
 * compressed, so a letter inside a zip is never squeezed much further, while a zip bomb is
 * squeezed thousands of times.
 */
export const LETTER_PACK_LIMITS = {
  /** The .zip itself, as uploaded. */
  maxPackBytes: 50 * 1024 * 1024,
  /** Letters listed in one manifest. */
  maxLetters: 100,
  /** Entries in the zip: the letters, the manifest and a Mac's own file details. */
  maxEntries: 250,
  /** One letter, unpacked (the template limit). */
  maxFileBytes: 10 * 1024 * 1024,
  /** `manifest.json`, unpacked. */
  maxManifestBytes: 256 * 1024,
  /** Everything in the pack, unpacked. */
  maxTotalBytes: 100 * 1024 * 1024,
  /** How many times smaller than its unpacked size an entry may be (past 64 KB). */
  maxRatio: 100,
} as const;

const Sha256 = z.string().regex(/^[0-9a-f]{64}$/, 'use the 64-character lowercase SHA-256');

/**
 * Who signs: their name, as a check, and one or more keys (APP-118 D3), or the MyRewards person ID.
 * Every key given must point at the same person, and the name must match their first and last
 * names. Never a display name alone: two people can share one.
 */
export const LetterPackSigner = z
  .object({
    name: z.string().trim().min(1).max(200),
    id: z.uuid().optional(),
    signInName: z.string().trim().min(3).max(254).optional(),
    email: z.email().max(254).optional(),
    eeNumber: z.string().trim().min(1).max(40).optional(),
    entraObjectId: z.guid().optional(),
  })
  .strict()
  .refine(
    (p) =>
      p.id !== undefined ||
      p.signInName !== undefined ||
      p.email !== undefined ||
      p.eeNumber !== undefined ||
      p.entraObjectId !== undefined,
    { message: 'add a person ID, sign-in name, work email, EE# or Entra object ID' },
  );
export type LetterPackSigner = z.infer<typeof LetterPackSigner>;

/**
 * One letter in the manifest. `variant`, `layout` and the file's hash follow the prepare tool's
 * manifest (`tools/letters/prepare-word-library.mjs`: `outputFile` → `file`, `preparedSha256` →
 * `sha256`), so a prepared batch zips straight in once its award type, name and signer are added.
 */
export const LetterPackEntry = z
  .object({
    $comment: z.string().max(1000).optional(),
    /** The file's name in the pack: no folders. */
    file: z.string().min(1).max(200),
    awardType: LetterLibraryType,
    name: z.string().trim().min(1).max(120),
    signer: LetterPackSigner,
    signerRole: LetterSignerRole,
    variant: LetterVariant,
    layout: LetterLayout,
    /** Template tag → merge field, for templates whose tags have other names. */
    fieldMap: z.record(z.string().max(64), z.string().max(64)).optional(),
    /** When given, the file must have exactly this SHA-256. */
    sha256: Sha256.optional(),
  })
  .strict();
export type LetterPackEntry = z.infer<typeof LetterPackEntry>;

/** What a pack's new letters come in as (APP-205): drafts, or approved once checked. */
export const LetterPackApproval = z.enum(['draft', 'approved']);
export type LetterPackApproval = z.infer<typeof LetterPackApproval>;

export const LetterPackManifest = z
  .object({
    /** A JSON Schema reference for editors; ignored by MyRewards. */
    $schema: z.string().max(500).optional(),
    $comment: z.union([z.string().max(4000), z.array(z.string().max(1000)).max(50)]).optional(),
    format: z.literal(LETTER_PACK_FORMAT),
    version: z.literal(LETTER_PACK_VERSION),
    /** A short name shown in the check, the result and the audit. */
    label: z.string().trim().min(1).max(80).optional(),
    /**
     * `draft` (or left out): every new letter comes in as a draft to review (APP-130 D20).
     * `approved` (APP-205, Tom 2026-10-27): each new letter is approved and used once its virus
     * check and sample are done; one that fails either stays a draft.
     */
    approval: LetterPackApproval.optional(),
    letters: z.array(LetterPackEntry).min(1).max(LETTER_PACK_LIMITS.maxLetters),
  })
  .strict();
export type LetterPackManifest = z.infer<typeof LetterPackManifest>;

/**
 * What happens to one file. `new`: the check found nothing wrong and it isn't in the library yet.
 * `already_in`: the same file is in the library (by its hash). `problem`: it can't be imported, and
 * `reason` says why. `imported`: the import added it as a draft (only in an import's result).
 */
export const LetterPackFileStatus = z.enum(['new', 'already_in', 'problem', 'imported']);
export type LetterPackFileStatus = z.infer<typeof LetterPackFileStatus>;

const PersonRef = z.object({ id: z.uuid(), name: z.string() });

/** The library version a file is already in as. */
export const LetterPackExisting = z.object({
  id: z.uuid(),
  awardType: LetterLibraryType,
  name: z.string(),
  version: z.number().int().positive(),
  status: LetterTemplateStatus,
  signer: PersonRef,
  variant: LetterVariant.optional(),
});
export type LetterPackExisting = z.infer<typeof LetterPackExisting>;

export const LetterPackFile = z.object({
  /** The name in the pack (for a manifest line with no file, the name it gave). */
  file: z.string(),
  status: LetterPackFileStatus,
  /** Plain words: why it's a problem, or where it's already in. Null for a plain new file. */
  reason: z.string().nullable(),
  /** A stable code for the reason. */
  code: z.string().nullable(),
  /** From the manifest; null when the file isn't in it. */
  awardType: LetterLibraryType.nullable(),
  name: z.string().nullable(),
  signer: PersonRef.nullable(),
  signerRole: LetterSignerRole.nullable(),
  variant: LetterVariant.nullable(),
  layout: LetterLayout.nullable(),
  format: LetterTemplateFormat.nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  sha256: z.string().nullable(),
  /** From the same check as an upload. */
  fieldsUsed: z.array(z.string()),
  requiredFonts: z.array(z.string()),
  notes: z.array(LetterTemplateNote),
  /** The coverage cell it fills (`SDB.<signerId>.cash_only`); null for "any" or unknown. */
  cell: z.string().nullable(),
  existing: LetterPackExisting.nullable(),
  /** The draft the import made (only for `imported`). */
  template: LetterTemplateView.nullable(),
});
export type LetterPackFile = z.infer<typeof LetterPackFile>;

const Count = z.number().int().nonnegative();

/** The dry run: nothing is saved. */
export const LetterPackCheck = z.object({
  /** The pack's SHA-256: the import must send it back, so it imports what was checked. */
  packSha256: Sha256,
  fileName: z.string(),
  label: z.string().nullable(),
  /** What the new letters come in as: the manifest's `approval`. */
  approval: LetterPackApproval,
  files: z.array(LetterPackFile),
  counts: z.object({ new: Count, alreadyIn: Count, problem: Count }),
  /** With malware scanning on, each new draft waits for its virus check before its sample. */
  scanRequired: z.boolean(),
});
export type LetterPackCheck = z.infer<typeof LetterPackCheck>;

export const LetterPackImported = z.object({
  /** The import's own id: on its audit entry, and on each sample made for it. */
  importId: z.uuid(),
  packSha256: Sha256,
  fileName: z.string(),
  label: z.string().nullable(),
  approval: LetterPackApproval,
  files: z.array(LetterPackFile),
  counts: z.object({ imported: Count, alreadyIn: Count, problem: Count }),
  /** Drafts whose sample a background job is making. */
  samplesQueued: Count,
  /** Of those, the ones it approves and uses once their check and sample are done (APP-205). */
  approvalsQueued: Count,
  scanRequired: z.boolean(),
});
export type LetterPackImported = z.infer<typeof LetterPackImported>;

/** The import's text field, sent with the pack as `file`: the hash its check returned. */
export const ImportLetterPackFields = z.object({ packSha256: Sha256 }).strict();
export type ImportLetterPackFields = z.infer<typeof ImportLetterPackFields>;
